import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCalendarPen,
  faCog,
  faDownload,
  faEnvelope,
  faFilePlus,
  faMinusCircle,
  faPaperclip,
  faPen,
  faSort,
  faTrashAlt,
} from '@fortawesome/pro-light-svg-icons'

import { useSetState } from '@domain-group/react-hooks'

import {
  Box, Button, IconWithMenu, Link,
} from '@domain-group/suit-theme'

import { confirmAction, confirmDelete } from '@functions/entity'

import type { CampaignQuotePayload } from '@hooks/useCampaignQuote'
import useCampaignQuoteTemplate from '@hooks/useCampaignQuoteTemplate'

import AuthenticatedDownload from '@components/AuthenticatedDownload'

const defaultState = {
  loading: false,
}

interface QuoteOptionsMenuProps {
  campaignQuotePayload: CampaignQuotePayload,
  locked: boolean,
}

const QuoteOptionsMenu = (props: QuoteOptionsMenuProps) => {
  const {
    campaignQuotePayload: {
      callbacks: {
        assignRequiredDates,
        createOrEditCampaignQuote,
        createInvoiceFromCampaignQuote: createInvoiceFn,
        editSectionSort,
        emailCampaignQuote,
        deleteCampaignQuote: deleteFn,
        updateCampaignQuote: updateFn,
      },
      campaignQuote: {
        agency_id,
        id: campaignQuoteId,
        void: isVoid,
      },
      // @ts-expect-error TS2339
      campaignQuoteTemplate: { disable_section_editing },
      isEditable,
      isPending,
      urls: {
        campaignQuotesIndexUrl,
        pdfDownloadUrl,
      },
    },
    locked,
  } = props

  const [state, setState] = useSetState(defaultState)
  const { loading } = state

  const campaignQuoteTemplatePayload = useCampaignQuoteTemplate({ agency_id, builder_version: 2 })
  const { callbacks: { saveQuoteAsTemplate } } = campaignQuoteTemplatePayload

  const createInvoicePayload = {
    callbacks: {
      actionFn: createInvoiceFn,
    },
    options: { shouldRedirect: true },
    strings: {
      actionString: 'create',
      entityName: 'invoice',
    },
  }

  const voidQuotePayload = {
    callbacks: {
      actionFn: updateFn,
    },
    options: {
      entityParams: { void: true },
      icon: 'warning',
      confirmButtonColor: '#DD6B55',
    },
    strings: {
      actionString: 'void',
      entityName: 'quote',
      toastText: 'Quote Voided',
    },
  }

  return (
    <IconWithMenu
      boxProps={{ position: 'relative' }}
      icon={<Button buttonStyle="secondaryUtility" icon={<FontAwesomeIcon icon={faCog} />} size="large" />}
    >
      {/*
       // @ts-expect-error TS2741 */}
      <IconWithMenu.Menu closeOnClick listStyle={{ right: 0 }}>
        {isEditable && (
          <Link key="edit" onClick={() => createOrEditCampaignQuote()}>
            <FontAwesomeIcon icon={faPen} /> Edit Quote Details
          </Link>
        )}

        {isEditable && (
          // @ts-expect-error TS2554
          <Link key="assign-dates" onClick={() => assignRequiredDates()}>
            <FontAwesomeIcon icon={faCalendarPen} /> Assign Item Dates
          </Link>
        )}

        <Link key="save" onClick={() => saveQuoteAsTemplate(campaignQuoteId)}>
          <FontAwesomeIcon icon={faFilePlus} /> Save as Template
        </Link>

        {!loading && (
          <Box onClick={() => setState({ loading: true })} key="download">
            <AuthenticatedDownload
              callbacks={{ afterAction: () => setState({ loading: false }) }}
              toastMessage="Quote generation in progress, please wait for download."
            >
              <Link key="download" href={pdfDownloadUrl} target="blank">
                <FontAwesomeIcon icon={faDownload} /> Download Quote
              </Link>
            </AuthenticatedDownload>
          </Box>
        )}

        {!isPending && (
          // @ts-expect-error TS2322
          <Link key="invoice" tabIndex="0" onClick={() => confirmAction(createInvoicePayload)}>
            <FontAwesomeIcon icon={faPaperclip} /> Create Invoice
          </Link>
        )}

        <Link key="email" onClick={() => emailCampaignQuote()}>
          <FontAwesomeIcon icon={faEnvelope} /> Email Quote
        </Link>

        {!isPending && !isVoid && (
          // @ts-expect-error TS2345
          <Link key="void" onClick={() => confirmAction(voidQuotePayload)}>
            <FontAwesomeIcon icon={faMinusCircle} /> Void Quote
          </Link>
        )}

        {!disable_section_editing && isEditable && (
          <Link onClick={() => editSectionSort()} key="sortSections" target="blank">
            <FontAwesomeIcon icon={faSort} /> Sort Sections
          </Link>
        )}

        {isEditable && !locked && (
          <Link
            as="a"
            key="delete"
            onClick={() => confirmDelete({
              callbacks: {
                afterAction: () => {
                  window.location.href = campaignQuotesIndexUrl
                },
                deleteFn,
              },
              strings: {
                confirmString: 'Are you sure? This cannot be undone.',
                entityName: 'Quote',
              },
            })}
            // @ts-expect-error TS2322
            tabIndex="0"
          >
            <FontAwesomeIcon icon={faTrashAlt} /> Delete Quote
          </Link>
        )}
      </IconWithMenu.Menu>
    </IconWithMenu>
  )
}

export default QuoteOptionsMenu
